import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CircularProgress } from '@mui/material';
import {
  ColumnDef,
  ColumnResizeMode,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { MAX_TABLE_ROWS } from 'constants/constants';

import EmptyState from 'components/EmptyState/EmptyState';

import { Table, TableBody, TableCell, TableFooter, TableRow } from '../ui';

import { DataTableHeader } from './components/DataTableHeader';
import DataTablePagination from './components/DataTablePagination';
import { DataTableRow } from './components/DataTableRow';
import { InfiniteScrollIndicator } from './components/InfiniteScrollIndicator';
import { IDataTableProps, IRowSelectionConfig, Size } from './DataTable.types';
import {
  geTableSelectionColumn,
  getContainerClassName,
  getTableClassName,
} from './DataTable.utils';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { useRowReorder } from './hooks/useRowReorder';
import { useTablePagination } from './hooks/useTablePagination';

export const DataTable = <T extends object>({
  data = [],
  columns,
  className,
  rowIdIdentifier = '',
  showHeader = true,
  size = 'sm',
  showPagination = true,
  rowsPerPage = MAX_TABLE_ROWS,
  bodyDataTestId = '',
  isLoading = false,
  emptyStateConfig,
  bordered = false,
  manualPagination = false,
  pageIndex: controlledPageIndex = 0,
  pageCount: controlledPageCount = 0,
  onPageChange,
  rowCount,
  rowSelectionConfig,
  getRowId,
  onRowClick,
  enableColumnResizing = false,
  stickyHeader = false,
  infiniteScrollConfig,
  enableRowReorder = false,
  onReorder,
  dragHandleColumnId: dragHandleColumnIdProp,
  sortingState: controlledSortingState,
  onSortingChange
}: IDataTableProps<T>): React.ReactElement => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionConfig?.selectedRowsState || {}
  );
  const [columnSizing, setColumnSizing] = useState({});
  const [internalSortingState, setInternalSortingState] =
    useState<SortingState>([]);

  const { enableRowSelection, onRowSelectionChange, isCheckboxesDisabled } =
    rowSelectionConfig ?? {};

  useEffect(() => {
    if (rowSelectionConfig?.selectedRowsState) {
      setRowSelection(rowSelectionConfig.selectedRowsState);
    }
  }, [rowSelectionConfig?.selectedRowsState]);

  const memoizedColumns = useMemo((): ColumnDef<T>[] => {
    if (!enableRowSelection) {
      return columns;
    }
    return [
      geTableSelectionColumn(isCheckboxesDisabled),
      ...columns
    ] as ColumnDef<T>[];
  }, [columns, enableRowSelection, isCheckboxesDisabled]);

  const hasSortingEnabled = useMemo((): boolean => {
    return memoizedColumns.some((column) => column.enableSorting);
  }, [memoizedColumns]);

  const memoizedData = useMemo((): T[] => data, [data]);
  const sortingState = controlledSortingState ?? internalSortingState;

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const nextSortingState =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(sortingState)
          : updaterOrValue;

      setInternalSortingState(nextSortingState);
      onSortingChange?.(nextSortingState);
    },
    [onSortingChange, sortingState]
  );

  const resolvedGetRowId = useMemo(() => {
    if (getRowId) {
      return getRowId;
    }
    if (rowIdIdentifier) {
      return (row: T): string =>
        String((row as Record<string, unknown>)[rowIdIdentifier] ?? '');
    }
    return undefined;
  }, [getRowId, rowIdIdentifier]);

  const { loadMoreRef } = useInfiniteScroll({
    infiniteScrollConfig,
    isLoading
  });

  const { sortableIds, sensors, handleDragEnd, dragHandleColumnId } =
    useRowReorder({
      data: memoizedData,
      columns: memoizedColumns,
      enableRowReorder,
      getRowId: resolvedGetRowId,
      rowIdIdentifier,
      onReorder,
      dragHandleColumnIdProp
    });

  const { paginationState, handlePaginationChange } = useTablePagination({
    manualPagination,
    controlledPageIndex,
    rowsPerPage,
    onPageChange
  });

  const getTableConfig = useMemo(() => {
    const baseConfig = {
      data: memoizedData,
      columns: memoizedColumns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      enableSorting: hasSortingEnabled,
      autoResetPageIndex: false,
      manualPagination,
      getRowId: resolvedGetRowId,
      state: {
        rowSelection,
        pagination: paginationState,
        sorting: sortingState
      },
      onPaginationChange: handlePaginationChange,
      onSortingChange: handleSortingChange
    };

    if (hasSortingEnabled) {
      Object.assign(baseConfig, { getSortedRowModel: getSortedRowModel() });
    }

    if (manualPagination) {
      Object.assign(baseConfig, { pageCount: controlledPageCount });
    }

    if (enableColumnResizing) {
      Object.assign(baseConfig, {
        enableColumnResizing: true,
        columnResizeMode: 'onChange' as ColumnResizeMode,
        onColumnSizingChange: setColumnSizing
      });
      Object.assign(baseConfig.state, { columnSizing });
    }

    return baseConfig;
  }, [
    memoizedData,
    memoizedColumns,
    hasSortingEnabled,
    manualPagination,
    resolvedGetRowId,
    rowSelection,
    paginationState,
    sortingState,
    controlledPageCount,
    enableColumnResizing,
    columnSizing,
    handlePaginationChange,
    handleSortingChange
  ]);

  const table = useReactTable({
    ...getTableConfig,
    onRowSelectionChange: (updater) => {
      let newSelection: RowSelectionState;

      if (typeof updater === 'function') {
        newSelection = updater(rowSelection);
      } else {
        newSelection = updater;
      }

      setRowSelection(newSelection);

      if (onRowSelectionChange) {
        const selectedRows = table
          .getPrePaginationRowModel()
          .rows.filter((row) => newSelection[row.id])
          .map((row) => row.original);
        onRowSelectionChange(selectedRows);
      }
    }
  });

  const renderLoadingState = useCallback((): React.ReactNode => {
    return (
      <div className="flex justify-center items-center w-full h-[200px]">
        <CircularProgress className="h-12" />
      </div>
    );
  }, []);

  const renderEmptyState = useCallback((): React.ReactElement | null => {
    const isVisible = emptyStateConfig?.isEmpty ?? false;

    if (!isVisible) {
      return null;
    }

    return (
      <EmptyState
        title={`Add your first ${emptyStateConfig!.title}`}
        description={emptyStateConfig!.description}
        buttonText={`Create New ${emptyStateConfig!.title}`}
        onClick={emptyStateConfig!.onClick}
        disableButton={emptyStateConfig!.disableButton}
      />
    );
  }, [emptyStateConfig]);

  const rowDragHandleColumnId = useMemo((): string | undefined => {
    if (enableRowReorder) {
      return dragHandleColumnId;
    }
    return undefined;
  }, [enableRowReorder, dragHandleColumnId]);

  const renderRows = useCallback((): React.ReactNode => {
    const rows = table.getRowModel().rows;

    return rows.map((row: Row<T>) => (
      <DataTableRow
        key={row.id}
        row={row}
        size={size}
        rowIdIdentifier={rowIdIdentifier}
        onRowClick={onRowClick}
        enableColumnResizing={enableColumnResizing}
        dragHandleColumnId={rowDragHandleColumnId}
      />
    ));
  }, [
    table,
    size,
    rowIdIdentifier,
    onRowClick,
    enableColumnResizing,
    rowDragHandleColumnId
  ]);

  const renderTableRows = useCallback((): React.ReactNode => {
    if (enableRowReorder) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {renderRows()}
          </SortableContext>
        </DndContext>
      );
    }

    return renderRows();
  }, [enableRowReorder, sensors, handleDragEnd, sortableIds, renderRows]);

  const renderNoResultsRow = useCallback((): React.ReactNode => {
    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="text-center py-10">
          No results.
        </TableCell>
      </TableRow>
    );
  }, [columns.length]);

  const renderTableBody = useCallback((): React.ReactNode => {
    const rows = table.getRowModel().rows;
    const hasRows = rows.length > 0;

    if (!hasRows) {
      return renderNoResultsRow();
    }

    const infiniteScrollEnabled = infiniteScrollConfig?.enabled || false;
    const infiniteScrollHasMore = infiniteScrollConfig?.hasMore || false;
    const infiniteScrollIsLoadingMore =
      infiniteScrollConfig?.isLoadingMore || false;

    return (
      <>
        {renderTableRows()}
        <InfiniteScrollIndicator
          columnsCount={columns.length}
          loadMoreRef={loadMoreRef}
          enabled={infiniteScrollEnabled}
          hasMore={infiniteScrollHasMore}
          isLoadingMore={infiniteScrollIsLoadingMore}
        />
      </>
    );
  }, [
    table,
    renderNoResultsRow,
    infiniteScrollConfig,
    renderTableRows,
    columns.length,
    loadMoreRef
  ]);

  const renderPagination = useCallback((): React.ReactNode => {
    if (!showPagination) {
      return null;
    }

    return (
      <TableFooter>
        <TableRow>
          <TableCell
            colSpan={columns.length}
            className="px-5 text-base-foreground text-p-s"
          >
            <DataTablePagination table={table} rowCount={rowCount} />
          </TableCell>
        </TableRow>
      </TableFooter>
    );
  }, [showPagination, columns.length, table, rowCount]);

  if (isLoading) {
    return <>{renderLoadingState()}</>;
  }

  if (emptyStateConfig?.isEmpty) {
    return <>{renderEmptyState()}</>;
  }

  return (
    <div className={getContainerClassName(bordered, stickyHeader)}>
      <Table
        disableScrollWrapper={stickyHeader}
        className={getTableClassName(enableColumnResizing, className)}
        style={{ tableLayout: 'fixed' }}
      >
        <DataTableHeader
          table={table}
          showHeader={showHeader}
          stickyHeader={stickyHeader}
          enableColumnResizing={enableColumnResizing}
        />
        <TableBody data-testid={bodyDataTestId}>{renderTableBody()}</TableBody>
        {renderPagination()}
      </Table>
    </div>
  );
};

export default DataTable;

export type { IDataTableProps, IRowSelectionConfig, Size };
